import { HandoffRuleRequestDto } from '@shared/services/apiService/apiService';
import {
  CLIENT_RULE_ID_KEY,
  DEFAULT_MESSAGE_GROUPS,
  DEFAULT_RULE_TYPE,
  HandoffRuleEditorValue,
  HandoffRuleType,
  JsonRecord,
  MessageGroupValue,
  VariableExtractionEnumValue,
  VariableExtractionFieldType,
  VariableExtractionRootSchema,
  VariableExtractionSchemaNode,
  VariableExtractionPlanFormValue,
} from './contracts';

const asRecord = (value: unknown): JsonRecord =>
  typeof value === 'object' && value !== null ? (value as JsonRecord) : {};

const newRuleId = () => `rule-${Math.random().toString(36).slice(2, 10)}`;
const VARIABLE_TYPES: VariableExtractionFieldType[] = [
  'string',
  'number',
  'integer',
  'boolean',
  'array',
  'object',
];
const toDashSeparated = (value: string) =>
  value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

const normalizeGroupedMessages = (messages: unknown): MessageGroupValue => {
  const source = asRecord(messages);
  return DEFAULT_MESSAGE_GROUPS.reduce<MessageGroupValue>((acc, group) => {
    const raw = source[group];
    acc[group] = Array.isArray(raw)
      ? raw.filter((item): item is string => typeof item === 'string')
      : [];
    return acc;
  }, {});
};

const isVariableType = (type: unknown): type is VariableExtractionFieldType =>
  VARIABLE_TYPES.includes(type as VariableExtractionFieldType);

const parseEnumValues = (value: unknown): VariableExtractionEnumValue[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const sanitized = value.filter(
    (item): item is VariableExtractionEnumValue =>
      typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean',
  );
  return sanitized.length > 0 ? sanitized : undefined;
};

const sanitizeSchemaNode = (rawNode: unknown): VariableExtractionSchemaNode | undefined => {
  const node = asRecord(rawNode);
  if (!isVariableType(node.type)) return undefined;
  const next: VariableExtractionSchemaNode = { type: node.type };
  if (typeof node.description === 'string' && node.description.trim())
    next.description = node.description.trim();
  const enumValues = parseEnumValues(node.enum);
  if (enumValues) next.enum = enumValues;

  if (node.type === 'object') {
    const rawProperties = asRecord(node.properties);
    const properties = Object.entries(rawProperties).reduce<
      Record<string, VariableExtractionSchemaNode>
    >((acc, [name, rawProperty]) => {
      const key = name.trim();
      if (!key) return acc;
      const sanitized = sanitizeSchemaNode(rawProperty);
      if (!sanitized) return acc;
      acc[key] = sanitized;
      return acc;
    }, {});
    next.properties = properties;
    const requiredSet = new Set(
      Array.isArray(node.required)
        ? node.required.filter((item): item is string => typeof item === 'string')
        : [],
    );
    next.required = Object.keys(properties).filter((name) => requiredSet.has(name));
    next.additionalProperties = false;
  }

  if (node.type === 'array') {
    const sanitizedItems = sanitizeSchemaNode(node.items);
    if (sanitizedItems) next.items = sanitizedItems;
  }

  return next;
};

const sanitizeRootSchema = (rawSchema: unknown): VariableExtractionRootSchema | undefined => {
  const schema = sanitizeSchemaNode(rawSchema);
  if (!schema || schema.type !== 'object') return undefined;
  return {
    type: 'object',
    properties: schema.properties ?? {},
    required: schema.required ?? [],
    additionalProperties: false,
    ...(schema.description ? { description: schema.description } : {}),
    ...(schema.enum ? { enum: schema.enum } : {}),
  };
};

const legacyFieldsToRootSchema = (fields: unknown): VariableExtractionRootSchema => {
  const fieldList = Array.isArray(fields) ? fields : [];
  const properties: Record<string, VariableExtractionSchemaNode> = {};
  const required: string[] = [];
  for (const rawField of fieldList) {
    const field = asRecord(rawField);
    const name = typeof field.name === 'string' ? field.name.trim() : '';
    if (!name) continue;
    const type = isVariableType(field.type) ? field.type : 'string';
    const node: VariableExtractionSchemaNode = { type };
    if (typeof field.description === 'string' && field.description.trim()) {
      node.description = field.description.trim();
    }
    const enumValues = parseEnumValues(field.enumValues);
    if (enumValues) node.enum = enumValues;
    properties[name] = node;
    if (field.required === true) required.push(name);
  }
  return {
    type: 'object',
    properties,
    required: Array.from(new Set(required)).filter((name) => Boolean(properties[name])),
    additionalProperties: false,
  };
};

export const serializeGroupedMessages = (messages: MessageGroupValue): MessageGroupValue =>
  DEFAULT_MESSAGE_GROUPS.reduce<MessageGroupValue>((acc, group) => {
    const groupMessages = messages[group] ?? [];
    // Keep in-progress values (including empty strings) so controlled inputs remain editable.
    acc[group] = groupMessages;
    return acc;
  }, {});

const normalizeVariableExtractionPlan = (
  plan: unknown,
): VariableExtractionPlanFormValue | undefined => {
  const planRecord = asRecord(plan);
  const schema = sanitizeRootSchema(planRecord.schema);
  if (schema) return { strict: true, schema };
  if (Array.isArray(planRecord.fields))
    return { strict: true, schema: legacyFieldsToRootSchema(planRecord.fields) };
  return undefined;
};

const serializeVariableExtractionPlan = (
  plan: VariableExtractionPlanFormValue | undefined,
): unknown => {
  if (!plan) return undefined;
  const schema = sanitizeRootSchema(plan.schema);
  if (!schema) return undefined;
  return {
    strict: true,
    schema,
  };
};

const normalizeContextSummaryPlan = (
  plan: unknown,
): HandoffRuleEditorValue['contextSummaryPlan'] => {
  const source = asRecord(plan);
  return {
    summary: typeof source.summary === 'string' ? source.summary : '',
  };
};

const normalizeSessionBoundaryPlan = (
  plan: unknown,
): HandoffRuleEditorValue['sessionBoundaryPlan'] => {
  const source = asRecord(plan);
  const startAgentId = typeof source.startAgentId === 'string' ? source.startAgentId : undefined;
  const endingAgentIds = Array.isArray(source.endingAgentIds)
    ? source.endingAgentIds.filter(
        (item): item is string => typeof item === 'string' && item.trim().length > 0,
      )
    : [];
  return {
    startAgentId,
    endingAgentIds: Array.from(new Set(endingAgentIds)),
    allowSessionWithoutEndingAgent:
      typeof source.allowSessionWithoutEndingAgent === 'boolean'
        ? source.allowSessionWithoutEndingAgent
        : true,
  };
};

type RuleTypeAdapter = {
  ruleType: HandoffRuleType;
  normalize: (raw: JsonRecord) => HandoffRuleEditorValue;
  serialize: (rule: HandoffRuleEditorValue) => HandoffRuleRequestDto;
};

const ensureSerializableRule = (base: JsonRecord): HandoffRuleRequestDto => {
  if (typeof base.fromAgentId !== 'string' || typeof base.toAgentId !== 'string') {
    throw new Error('Invalid handoff rule payload.');
  }
  return base as HandoffRuleRequestDto;
};

const toSerializableRulePayload = (rule: HandoffRuleEditorValue): JsonRecord => {
  const normalizedName = toDashSeparated(rule.name);
  return {
    ruleType: rule.ruleType,
    name: normalizedName || undefined,
    description: rule.description.trim() || undefined,
    fromAgentId: rule.fromAgentId,
    toAgentId: rule.toAgentId,
    messages: serializeGroupedMessages(rule.messages),
    contextPropagationPlan: rule.contextPropagationPlan,
    contextSummaryPlan: {
      summary: rule.contextSummaryPlan?.summary?.trim() ?? '',
    },
    sessionBoundaryPlan: {
      startAgentId: rule.sessionBoundaryPlan?.startAgentId || undefined,
      endingAgentIds: Array.from(new Set(rule.sessionBoundaryPlan?.endingAgentIds ?? [])),
      allowSessionWithoutEndingAgent:
        rule.sessionBoundaryPlan?.allowSessionWithoutEndingAgent ?? true,
    },
    variableExtractionPlan: serializeVariableExtractionPlan(rule.variableExtractionPlan),
  };
};

const agentHandoffAdapter: RuleTypeAdapter = {
  ruleType: DEFAULT_RULE_TYPE,
  normalize: (raw) => {
    const clientRuleId =
      typeof raw[CLIENT_RULE_ID_KEY] === 'string' && raw[CLIENT_RULE_ID_KEY]
        ? (raw[CLIENT_RULE_ID_KEY] as string)
        : newRuleId();
    return {
      id: clientRuleId,
      clientRuleId,
      ruleType: DEFAULT_RULE_TYPE,
      name: typeof raw.name === 'string' ? raw.name : '',
      description: typeof raw.description === 'string' ? raw.description : '',
      fromAgentId: typeof raw.fromAgentId === 'string' ? raw.fromAgentId : '',
      toAgentId: typeof raw.toAgentId === 'string' ? raw.toAgentId : '',
      messages: normalizeGroupedMessages(raw.messages),
      contextPropagationPlan:
        typeof raw.contextPropagationPlan === 'object' && raw.contextPropagationPlan !== null
          ? (raw.contextPropagationPlan as HandoffRuleEditorValue['contextPropagationPlan'])
          : undefined,
      contextSummaryPlan: normalizeContextSummaryPlan(raw.contextSummaryPlan),
      sessionBoundaryPlan: normalizeSessionBoundaryPlan(raw.sessionBoundaryPlan),
      variableExtractionPlan: normalizeVariableExtractionPlan(raw.variableExtractionPlan),
      raw,
    };
  },
  serialize: (rule) => {
    const base = toSerializableRulePayload(rule);
    return ensureSerializableRule(base);
  },
};

const adaptersByType: Record<string, RuleTypeAdapter> = {
  [DEFAULT_RULE_TYPE]: agentHandoffAdapter,
};

export const resolveRuleAdapter = (raw: JsonRecord): RuleTypeAdapter => {
  const rawRuleType = typeof raw.ruleType === 'string' ? raw.ruleType : DEFAULT_RULE_TYPE;
  return adaptersByType[rawRuleType] ?? agentHandoffAdapter;
};

export const createEmptyHandoffRule = (): HandoffRuleEditorValue => {
  const clientRuleId = newRuleId();
  return {
    id: clientRuleId,
    clientRuleId,
    ruleType: DEFAULT_RULE_TYPE,
    name: '',
    description: '',
    fromAgentId: '',
    toAgentId: '',
    messages: DEFAULT_MESSAGE_GROUPS.reduce<MessageGroupValue>((acc, group) => {
      acc[group] = [];
      return acc;
    }, {}),
    contextPropagationPlan: { type: 'all' },
    contextSummaryPlan: { summary: '' },
    sessionBoundaryPlan: {
      startAgentId: undefined,
      endingAgentIds: [],
      allowSessionWithoutEndingAgent: true,
    },
    variableExtractionPlan: {
      strict: true,
      schema: {
        type: 'object',
        properties: {},
        required: [],
        additionalProperties: false,
      },
    },
    raw: { [CLIENT_RULE_ID_KEY]: clientRuleId, ruleType: DEFAULT_RULE_TYPE },
  };
};

export { asRecord, normalizeGroupedMessages };
